/**
 * Multi-tenant data fetching layer
 * All API calls include ?client={slug}
 */

function apiUrl(endpoint, slug, range) {
  let url = `/api/${endpoint}?client=${slug}`;
  if (range) url += `&range=${range}`;
  return url;
}

export function createFetcher(slug) {
  return {
    async checkAuth() {
      const res = await fetch(apiUrl('auth', slug));
      return res.ok;
    },

    async login(password) {
      const res = await fetch(apiUrl('auth', slug), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      return res.ok;
    },

    async fetchSummary(range) {
      try {
        const res = await fetch(apiUrl('summary', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching summary:', error);
        return { enquiries: 0, cost: 0, costPerEnquiry: 0, target: null, currency: '$' };
      }
    },

    async fetchLocations(range) {
      try {
        const res = await fetch(apiUrl('locations', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching locations:', error);
        return { locations: [], brand: null };
      }
    },

    async fetchAdPerformance(range) {
      try {
        const res = await fetch(apiUrl('ad-performance', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching ad performance:', error);
        return { topAds: [], bottomAds: [], summary: { totalAds: 0 } };
      }
    },

    async fetchMonthlyTrend() {
      try {
        const res = await fetch(apiUrl('monthly-trend', slug));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching monthly trend:', error);
        return { monthlyTrend: [] };
      }
    },

    async fetchTopKeywords(range) {
      try {
        const res = await fetch(apiUrl('top-keywords', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching top keywords:', error);
        return { keywords: [] };
      }
    },

    async fetchManagerNotes() {
      try {
        const res = await fetch(apiUrl('manager-notes', slug));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        return { updatedAt: null, note: null };
      }
    },

    async fetchAccountHealth(range) {
      try {
        const res = await fetch(apiUrl('account-health', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching account health:', error);
        return null;
      }
    },

    async fetchHourlyPerformance(range) {
      try {
        const res = await fetch(apiUrl('hourly-performance', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching hourly performance:', error);
        return { heatmapData: [] };
      }
    },

    async fetchDevicePerformance(range) {
      try {
        const res = await fetch(apiUrl('device-performance', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching device performance:', error);
        return { devices: [] };
      }
    },

    // --- New endpoints for Phase 2 ---

    async fetchBudgetPacing() {
      try {
        const res = await fetch(apiUrl('budget-pacing', slug));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching budget pacing:', error);
        return { available: false };
      }
    },

    async fetchImpressionShare(range) {
      try {
        const res = await fetch(apiUrl('impression-share', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching impression share:', error);
        return null;
      }
    },

    async fetchConversionLag() {
      try {
        const res = await fetch(apiUrl('conversion-lag', slug));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching conversion lag:', error);
        return { buckets: [] };
      }
    },

    async fetchLandingPages(range) {
      try {
        const res = await fetch(apiUrl('landing-pages', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching landing pages:', error);
        return { pages: [] };
      }
    },

    async fetchGeographic(range) {
      try {
        const res = await fetch(apiUrl('geographic', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching geographic:', error);
        return { locations: [] };
      }
    },

    async fetchQualityScore() {
      try {
        const res = await fetch(apiUrl('quality-score', slug));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching quality score:', error);
        return null;
      }
    },

    async fetchAdAssetPerformance(range) {
      try {
        const res = await fetch(apiUrl('ad-asset-performance', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching ad asset performance:', error);
        return { assets: [] };
      }
    },

    // --- Phase 3 endpoints ---

    async fetchDailyTrend(range) {
      try {
        const res = await fetch(apiUrl('daily-trend', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching daily trend:', error);
        return { days: [] };
      }
    },

    async fetchCampaignTypeBreakdown(range) {
      try {
        const res = await fetch(apiUrl('campaign-type-breakdown', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching campaign type breakdown:', error);
        return { types: [] };
      }
    },

    async fetchSearchTermIntent(range) {
      try {
        const res = await fetch(apiUrl('search-term-intent', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching search term intent:', error);
        return { categories: [] };
      }
    },

    async fetchAdScheduleInsights(range) {
      try {
        const res = await fetch(apiUrl('ad-schedule-insights', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching ad schedule insights:', error);
        return { lowHours: [], potentialSavings: 0 };
      }
    },

    async fetchDayOfWeek(range) {
      try {
        const res = await fetch(apiUrl('day-of-week', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching day of week:', error);
        return { days: [] };
      }
    },

    async fetchConversionActions(range) {
      try {
        const res = await fetch(apiUrl('conversion-actions', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching conversion actions:', error);
        return { actions: [] };
      }
    },

    async fetchNetworkBreakdown(range) {
      try {
        const res = await fetch(apiUrl('network-breakdown', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching network breakdown:', error);
        return { networks: [] };
      }
    },

    // --- Phase 4 endpoints ---

    async fetchSearchTermMovers(range) {
      try {
        const res = await fetch(apiUrl('search-term-movers', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching search term movers:', error);
        return { rising: [], new: [], dropped: [] };
      }
    },

    async fetchAuctionInsights(range) {
      try {
        const res = await fetch(apiUrl('auction-insights', slug, range));
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        return await res.json();
      } catch (error) {
        console.error('Error fetching auction insights:', error);
        return { competitors: [], you: null };
      }
    }
  };
}
